'use client';
import SquareSkeletonLoader from '@app/components/square-skeleton-loader/square-skeleton-loader';
import { generateTheme } from '@app/lib/helpers/generate-theme';
import proxyImageUrlIfEnabled from '@app/lib/images/proxy-image-url-if-enabled';
import {
    getNostoContentPlacement,
    recordNostoContentClick,
} from '@app/lib/nosto/content-attribution';
import { useScript } from '@app/scripts';
import s from '@app/slices/NostoPoster/NostoPoster.module.css';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import cn from 'clsx';
import {
    CTAIcon,
    CTASet,
    PatternCTASet,
    PatternTitleSubtitle,
    StaticImage,
    Video,
} from 'mmds';
import { useParams } from 'next/navigation';
import { useLayoutEffect, useState } from 'react';

// A CTA icon from Nosto can be a named mmds icon (e.g. "northeast") or an image URL.
const resolveCtaIcon = (value?: string) => {
    if (!value) return {};
    return /^https?:\/\//i.test(value)
        ? { iconUrl: value }
        : { icon: value.toLowerCase() as CTAIcon };
};

/**
 * Props for `NostoPoster`.
 */
export type NostoPosterProps = SliceComponentProps<Content.NostoPosterSlice>;

/**
 * Component for "NostoPoster" Slices.
 */
const NostoPoster = ({ slice }: NostoPosterProps): JSX.Element | null => {
    const [ready, setReady] = useState(false);
    const { lang, collection } = useParams();
    const { nostoInitialised } = useScript();
    const [billboardStaticContent, setBillboardStaticContent] =
        useState<any>(false);
    const [nostoResultId, setNostoResultId] = useState<string | null>(null);
    const placementIdentifier = slice?.primary.placement_id as string;
    const { desktop_height, desktop_width, mobile_height, mobile_width } =
        slice?.primary;

    useLayoutEffect(() => {
        let cancelled = false;
        let unavailableTimeout: number | undefined;

        const markReady = () => {
            if (!cancelled) setReady(true);
        };

        if (!placementIdentifier) {
            markReady();
            return () => {
                cancelled = true;
            };
        }

        if (!nostoInitialised || !(window as any).nostojs) {
            unavailableTimeout = window.setTimeout(markReady, 3000);
            return () => {
                cancelled = true;
                if (unavailableTimeout !== undefined) {
                    window.clearTimeout(unavailableTimeout);
                }
            };
        }

        setReady(false);
        unavailableTimeout = window.setTimeout(markReady, 5000);

        try {
            (window as any)?.nostojs((api) => {
                const session = api.defaultSession();
                session
                    .viewOther(session)
                    .setPlacements([placementIdentifier])
                    .load()
                    .then((data) => {
                        const content = getNostoContentPlacement(
                            data,
                            placementIdentifier
                        );
                        if (!content?.html) {
                            markReady();
                            return;
                        }
                        const json = JSON.parse(content.html);
                        if (!json) {
                            markReady();
                            return;
                        }
                        if (cancelled) return;
                        setBillboardStaticContent(json);
                        setNostoResultId(content.result_id ?? null);
                        if (unavailableTimeout !== undefined) {
                            window.clearTimeout(unavailableTimeout);
                        }
                        setReady(true);
                    })
                    .catch((e) => {
                        if (unavailableTimeout !== undefined) {
                            window.clearTimeout(unavailableTimeout);
                        }
                        markReady();
                        if (process.env.NODE_ENV !== 'production') {
                            console.log('Error fetching Nosto content', e);
                        }
                    });
            });
        } catch (e) {
            markReady();
            if (process.env.NODE_ENV !== 'production') {
                console.log('Error fetching or parsing Nosto content', e);
            }
        }

        return () => {
            cancelled = true;
            if (unavailableTimeout !== undefined) {
                window.clearTimeout(unavailableTimeout);
            }
        };
    }, [nostoInitialised, collection, lang, placementIdentifier]);

    if (!ready) {
        return (
            <>
                <div className={s.desktopLoader}>
                    <SquareSkeletonLoader
                        uniquekey={'nosto-poster-loader-desktop'}
                        width={desktop_width || 1920}
                        height={desktop_height || 960}
                    />
                </div>
                <div className={s.mobileLoader}>
                    <SquareSkeletonLoader
                        uniquekey={'nosto-poster-loader-mobile'}
                        height={mobile_height || 1150}
                        width={mobile_width || 767}
                    />
                </div>
            </>
        );
    }

    if (!billboardStaticContent) return null;

    const {
        title,
        content,
        mobileTheme,
        desktopTheme,
        desktopImage,
        mobileImage,
        desktopVideoURL,
        mobileVideoURL,
        backgroundColor,
        cta1Label,
        cta1Link,
        cta1Variant,
        cta1Icon,
        cta2Label,
        cta2Link,
        cta2Variant,
        cta2Icon,
        cta3Label,
        cta3Link,
        cta3Variant,
        cta3Icon,
        contentPosition,
        fallbackLink,
        fallbackOpenInNewTab,
    } = billboardStaticContent;

    const theme = generateTheme({
        mobileTheme,
        desktopTheme,
    });

    const recordCtaAttribution = () => {
        recordNostoContentClick(nostoResultId);
    };

    const ctaSet: CTASet = [
        {
            label: cta1Label,
            hrefLink: cta1Link,
            variant: cta1Variant,
            ...resolveCtaIcon(cta1Icon),
            onClick: recordCtaAttribution,
        },
        {
            label: cta2Label,
            hrefLink: cta2Link,
            variant: cta2Variant,
            ...resolveCtaIcon(cta2Icon),
            onClick: recordCtaAttribution,
        },
        {
            label: cta3Label,
            hrefLink: cta3Link,
            variant: cta3Variant,
            ...resolveCtaIcon(cta3Icon),
            onClick: recordCtaAttribution,
        },
    ];

    const contentPositionWithDefault = contentPosition || 'bottomLeft';

    const contentLayoutClassname = cn({
        [s.bottomLeft]: contentPositionWithDefault === 'bottomLeft',
        [s.center]: contentPosition === 'center',
        [s.topLeft]: contentPosition === 'topLeft',
    });
    const buttonContentLayout = cn(s.buttonRoot, {
        [s.buttonBottomLeft]: contentPositionWithDefault === 'bottomLeft',
        [s.buttonCenter]: contentPosition === 'center',
        [s.buttonTopLeft]: contentPosition === 'topLeft',
    });
    const identifier = `bb_static_poster`;
    const hasVideo = Boolean(desktopVideoURL || mobileVideoURL);

    return (
        <div
            key={identifier}
            data-testid={identifier}
            className={cn(s.billboardStaticRoot)}
            data-theme={theme}
        >
            {fallbackLink && (
                <a
                    href={fallbackLink}
                    className={s.fallbackLink}
                    aria-label={title || 'View'}
                    onClick={recordCtaAttribution}
                    target={fallbackOpenInNewTab ? '_blank' : undefined}
                    rel={fallbackOpenInNewTab ? 'noreferrer' : undefined}
                />
            )}
            {hasVideo ? (
                <Video
                    desktopVideoUrl={desktopVideoURL || mobileVideoURL}
                    mobileVideoUrl={mobileVideoURL || desktopVideoURL}
                    poster={desktopImage}
                    controls={false}
                    muted={true}
                    loop={true}
                    autoplay={true}
                />
            ) : (
                <StaticImage
                    desktopImage={desktopImage}
                    mobileImage={mobileImage}
                    altDescription={title || 'Nosto Poster'}
                    fullWidth={true}
                    urlTransformer={proxyImageUrlIfEnabled}
                />
            )}
            <div className={`${s.overlayContent} ${contentLayoutClassname}`}>
                <div className={`${s.content} ${contentLayoutClassname}`}>
                    <PatternTitleSubtitle
                        title={title}
                        titleSize="large"
                        subTitle={content}
                        subTitleSize="medium"
                        billboardType="static"
                    />
                    <div className={buttonContentLayout}>
                        {ctaSet && (
                            <PatternCTASet set={ctaSet} lang={lang as string} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NostoPoster;
